/**
 * spreadGraph — the "#spread" social-graph animation shared by the two
 * marketing landings.
 *
 * One post → friends repost → their circles see it → yeses come back →
 * one chain lights up and resolves into a date. The timeline is plain
 * timers so it also lands its final frame in capture tools.
 *
 * Ported verbatim from the Claude Design source of truth: the second
 * script block of WYDM Landing.html / WYDM Agents Landing.html. Those
 * two scripts are the same routine and differ in exactly four spots,
 * which are the `Variant` fields below:
 *
 *   | field     | / (human)                     | /agents                      |
 *   |-----------|-------------------------------|------------------------------|
 *   | yesIcon   | filled heart                  | stroked check                |
 *   | relayWord | "reposted"                    | "relayed"                    |
 *   | label     | "Jamie"                       | "@jamie"                     |
 *   | token     | circle                        | rounded rect                 |
 *   | chipText  | "You posted your card…"       | 'agent.publish({ card: … })' |
 *
 * Everything else — layout maths, node/edge construction, the timeline,
 * the compact (<880px) relayout, the counters, reduced-motion handling —
 * is identical in both design files and lives here once.
 *
 * Returns a teardown that clears every timer/observer/listener, so the
 * React effects that own it can clean up on unmount.
 */

export interface SpreadGraphVariant {
  /** Inner SVG of the ".yes" verdict pip on a second-ring node. */
  yesIcon: string;
  /** Caption shown on a first-ring node once it passes the card on. */
  relayWord: string;
  /** Renders the visible label for a node given its display name. */
  label: (name: string) => string;
  /** Builds the token that rides an edge while it draws. */
  token: (ns: string, hot: boolean) => SVGElement;
  /** Status-chip copy, wide and compact. */
  chipText: { wide: string; compact: string };
}

interface Kid {
  n: string;
  g: number;
  hot?: boolean;
}
interface Friend {
  n: string;
  a: number;
  g: number;
  kids: Kid[];
}
/** An edge before its node has been built (see `build`). */
interface EdgeBase {
  path: SVGPathElement;
  tok: SVGElement;
  mo: SVGElement;
}
type Edge = EdgeBase & { el: HTMLElement };

const FRIENDS: Friend[] = [
  { n: 'Jamie', a: 200, g: 4, kids: [{ n: 'Charlie', g: 2 }, { n: 'Remy', g: 4, hot: true }, { n: 'Frankie', g: 6 }] },
  { n: 'Alex', a: 252, g: 3, kids: [{ n: 'Emery', g: 5 }, { n: 'Robin', g: 1 }] },
  { n: 'Jordan', a: 310, g: 6, kids: [{ n: 'Nico', g: 3 }, { n: 'Sage', g: 2 }, { n: 'Blake', g: 5 }] },
  { n: 'Kai', a: 20, g: 2, kids: [{ n: 'Devon', g: 6 }, { n: 'Harper', g: 3 }] },
  { n: 'Morgan', a: 108, g: 5, kids: [{ n: 'Elliot', g: 1 }, { n: 'Skyler', g: 4 }, { n: 'Ari', g: 2 }] },
];

export function mountSpreadGraph(root: ParentNode, variant: SpreadGraphVariant): () => void {
  const graph = root.querySelector<HTMLElement>('#graph');
  const outer = root.querySelector<HTMLElement>('#graphOuter');
  const svg = root.querySelector<SVGSVGElement>('#gedges');
  if (!graph || !outer || !svg) return () => {};

  const ns = 'http://www.w3.org/2000/svg';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 1100;
  let H = 560;
  let C = { x: 550, y: 280 };
  let compact = false;
  const rad = (d: number) => (d * Math.PI) / 180;
  const at = (a: number, rx: number, ry: number) => ({ x: C.x + rx * Math.cos(rad(a)), y: C.y + ry * Math.sin(rad(a)) });

  let ring1: Edge[] = [];
  let ring2: Edge[] = [];
  let edges: Edge[] = [];
  let eid = 0;
  let saidYes: number[] = [];

  function node(p: { x: number; y: number }, size: number, name: string, grad: number, hot: boolean) {
    const el = document.createElement('div');
    el.className = 'gnode' + (hot ? ' hot' : '');
    el.style.cssText = 'left:' + p.x + 'px;top:' + p.y + 'px;z-index:' + (size > 45 ? 5 : 4);
    // Static markup only: the icon comes from the module-level variant and
    // `name` from the hardcoded FRIENDS table. Nothing here is user input.
    el.innerHTML =
      '<div class="gav photo-gradient-' + grad + '" style="width:' + size + 'px;height:' + size + 'px"></div>' +
      '<span class="yes">' + variant.yesIcon + '</span>' +
      '<span class="rp">' + variant.relayWord + '</span><span class="lbl">' + variant.label(name) + '</span>';
    graph!.appendChild(el);
    return el;
  }

  function edge(a: { x: number; y: number }, b: { x: number; y: number }, hot: boolean) {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const cx = mx - (dy / len) * 22;
    const cy = my + (dx / len) * 22;
    const p = document.createElementNS(ns, 'path');
    p.setAttribute('d', 'M' + a.x + ' ' + a.y + ' Q' + cx + ' ' + cy + ' ' + b.x + ' ' + b.y);
    p.setAttribute('class', 'edge' + (hot ? ' hot' : ''));
    p.setAttribute('id', 'ge' + ++eid);
    svg!.appendChild(p);
    const L = p.getTotalLength ? p.getTotalLength() : 400;
    p.style.setProperty('--len', String(L));
    p.style.strokeDasharray = String(L);
    /* token that rides the edge when it draws */
    const tok = variant.token(ns, hot);
    tok.setAttribute('fill', 'var(--coral)');
    tok.setAttribute('opacity', '0');
    const mo = document.createElementNS(ns, 'animateMotion');
    mo.setAttribute('dur', '.75s');
    mo.setAttribute('begin', 'indefinite');
    mo.setAttribute('fill', 'freeze');
    const mp = document.createElementNS(ns, 'mpath');
    mp.setAttribute('href', '#ge' + eid);
    mp.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#ge' + eid);
    mo.appendChild(mp);
    tok.appendChild(mo);
    svg!.appendChild(tok);
    return { path: p, tok, mo };
  }

  /* The graph is laid out twice: a wide 1100×560 version for desktop, and a
     taller one sized to the actual viewport on phones — scaling the wide one
     down would shrink the payoff card to an unreadable smudge. */
  function build() {
    const ow = outer!.clientWidth || 1100;
    compact = ow < 880;
    graph!.classList.toggle('cmp', compact);
    W = compact ? Math.max(300, Math.min(ow, 620)) : 1100;
    H = compact ? 700 : 560;
    C = { x: W / 2, y: compact ? 345 : 280 };
    graph!.style.setProperty('--gw', W + 'px');
    graph!.style.setProperty('--gh', H + 'px');
    graph!.style.setProperty('--cx', C.x + 'px');
    graph!.style.setProperty('--cy', C.y + 'px');
    graph!.style.setProperty('--ccw', (compact ? 132 : 150) + 'px');
    svg!.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    Array.prototype.slice.call(graph!.querySelectorAll('.gnode')).forEach((n: HTMLElement) => {
      n.parentNode!.removeChild(n);
    });
    while (svg!.firstChild) svg!.removeChild(svg!.firstChild);
    ring1 = [];
    ring2 = [];
    eid = 0;
    const r1x = compact ? W * 0.31 : 258;
    const r1y = compact ? 196 : 178;
    const r2x = compact ? W * 0.44 : 468;
    const r2y = compact ? 292 : 246;
    const s1 = compact ? 46 : 56;
    const s2 = compact ? 34 : 40;
    const spreadDeg = compact ? 34 : 26;
    const chip = root.querySelector('#gchipTxt');
    if (chip) chip.textContent = compact ? variant.chipText.compact : variant.chipText.wide;
    // Edge before node, matching the design's DOM append order.
    FRIENDS.forEach((f) => {
      const p = at(f.a, r1x, r1y);
      const hot = f.n === 'Jamie';
      const e = edge(C, p, hot);
      ring1.push({ ...e, el: node(p, s1, f.n, f.g, hot) });
      const kids = compact ? f.kids.filter((k, i) => k.hot || i === 0).slice(0, 2) : f.kids;
      const spread = (kids.length - 1) / 2;
      kids.forEach((k, i) => {
        const kp = at(f.a + (i - spread) * spreadDeg, r2x, r2y);
        const ke = edge(p, kp, !!k.hot);
        ring2.push({ ...ke, el: node(kp, s2, k.n, k.g, !!k.hot) });
      });
    });
    edges = ring1.concat(ring2);
    saidYes = (compact ? [0, 1, 3, 5] : [0, 1, 3, 4, 6, 7, 9, 11]).filter((i) => i < ring2.length);
  }

  function fireEdge(e: Edge) {
    e.path.classList.add('drawn');
    if (reduce) return;
    e.tok.setAttribute('opacity', '1');
    try {
      (e.mo as SVGAnimationElement).beginElement();
    } catch {
      /* beginElement throws in browsers without SMIL — the edge still draws */
    }
    setTimeout(() => e.tok.setAttribute('opacity', '0'), 760);
  }

  const chipEl = root.querySelector('#spread .gchip');
  const counters = {
    seen: root.querySelector('#s-seen'),
    yes: root.querySelector('#s-yes'),
    date: root.querySelector('#s-date'),
  };
  let timers: ReturnType<typeof setTimeout>[] = [];
  function T(ms: number, fn: () => void) {
    timers.push(setTimeout(fn, reduce ? 0 : ms));
  }
  function count(el: Element | null, to: number, ms: number) {
    if (!el) return;
    if (reduce) {
      el.textContent = String(to);
      return;
    }
    const steps = Math.max(1, Math.round(ms / 32));
    let i = 0;
    const tick = () => {
      i++;
      const p = Math.min(1, i / steps);
      el.textContent = String(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) timers.push(setTimeout(tick, 32));
    };
    tick();
  }

  function finalFrame() {
    graph!.classList.add('lit', 'payoff', 'done');
    edges.forEach((e) => {
      e.path.classList.add('drawn');
      e.tok.setAttribute('opacity', '0');
    });
    ring1.concat(ring2).forEach((e) => e.el.classList.add('on'));
    ring1.forEach((e) => e.el.classList.add('shared'));
    saidYes.forEach((i) => {
      if (ring2[i]) ring2[i].el.classList.add('said');
    });
    if (chipEl) chipEl.classList.remove('on');
    if (counters.seen) counters.seen.textContent = '412';
    if (counters.yes) counters.yes.textContent = '141';
    if (counters.date) counters.date.textContent = '1';
  }

  function reset() {
    timers.forEach(clearTimeout);
    timers = [];
    graph!.classList.remove('lit', 'payoff', 'done');
    if (chipEl) chipEl.classList.remove('on');
    edges.forEach((e) => {
      e.path.classList.remove('drawn');
      e.tok.setAttribute('opacity', '0');
      e.el.classList.remove('on', 'said', 'shared');
    });
    (Object.keys(counters) as (keyof typeof counters)[]).forEach((k) => {
      if (counters[k]) counters[k]!.textContent = '0';
    });
  }

  function play() {
    reset();
    looping = true;
    if (reduce) {
      finalFrame();
      looping = false;
      return;
    }
    T(80, () => {
      graph!.classList.add('lit');
      if (chipEl) chipEl.classList.add('on');
    });
    T(700, () => ring1.forEach((e, i) => T(i * 90, () => fireEdge(e))));
    T(1100, () => ring1.forEach((e, i) => T(i * 90, () => e.el.classList.add('on'))));
    T(1750, () => ring1.forEach((e, i) => T(i * 70, () => e.el.classList.add('shared'))));
    T(2000, () => ring2.forEach((e, i) => T(i * 55, () => fireEdge(e))));
    T(2400, () => ring2.forEach((e, i) => T(i * 55, () => e.el.classList.add('on'))));
    T(2600, () => count(counters.seen, 412, 1500));
    T(3300, () =>
      saidYes.forEach((n, i) =>
        T(i * 130, () => {
          if (ring2[n]) ring2[n].el.classList.add('said');
        }),
      ),
    );
    T(3400, () => count(counters.yes, 141, 1400));
    T(5000, () => {
      graph!.classList.add('payoff');
      if (chipEl) chipEl.classList.remove('on');
    });
    T(5500, () => {
      graph!.classList.add('done');
      count(counters.date, 1, 300);
    });
    /* hold the payoff, then run the whole story again */
    timers.push(
      setTimeout(() => {
        looping = false;
        if (visible) play();
        else finalFrame();
      }, 9200),
    );
  }

  function fit() {
    const s = compact ? 1 : Math.min(0.84, (outer!.clientWidth || 1100) / 1100);
    graph!.style.transform = 'scale(' + s + ')';
    outer!.style.height = Math.round((H + 40) * s) + 'px';
  }
  build();
  fit();
  let lastW = outer.clientWidth;
  let rt: ReturnType<typeof setTimeout>;
  const onResize = () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      const ow = outer!.clientWidth;
      const modeChanged = ow < 880 !== compact;
      if (modeChanged || (compact && Math.abs(ow - lastW) > 30)) {
        lastW = ow;
        build();
        fit();
        if (started) play();
      } else {
        fit();
      }
    }, 160);
  };
  addEventListener('resize', onResize);

  let started = false;
  let visible = false;
  let looping = false;
  const io = new IntersectionObserver(
    (en) => {
      en.forEach((e) => {
        visible = e.isIntersecting;
        if (visible && !looping) {
          started = true;
          play();
        }
      });
    },
    { threshold: 0.25 },
  );
  io.observe(graph);
  const kick = setTimeout(() => {
    if (!started) {
      started = true;
      visible = true;
      play();
    }
  }, 2500);

  return () => {
    io.disconnect();
    removeEventListener('resize', onResize);
    clearTimeout(rt);
    clearTimeout(kick);
    timers.forEach(clearTimeout);
    timers = [];
  };
}

/** Variant used by the human landing (/). */
export const HUMAN_VARIANT: SpreadGraphVariant = {
  yesIcon:
    '<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M12 21s-7.5-4.6-10-9.1C.4 8.8 1.6 5 5 5c2 0 3.2 1.2 4 2.4C9.8 6.2 11 5 13 5c3.4 0 4.6 3.8 3 6.9C18.5 16.4 12 21 12 21z"/></svg>',
  relayWord: 'reposted',
  label: (name) => name,
  token: (ns, hot) => {
    const tok = document.createElementNS(ns, 'circle') as SVGElement;
    tok.setAttribute('r', hot ? '6' : '4.5');
    return tok;
  },
  chipText: {
    wide: 'You posted your card to your story',
    compact: 'Posted to your story',
  },
};

/** Variant used by the agent platform landing (/agents). */
export const AGENT_VARIANT: SpreadGraphVariant = {
  yesIcon:
    '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7"/></svg>',
  relayWord: 'relayed',
  label: (name) => '@' + name.toLowerCase(),
  token: (ns, hot) => {
    const tok = document.createElementNS(ns, 'rect') as SVGElement;
    const ts = hot ? 11 : 8;
    tok.setAttribute('width', String(ts));
    tok.setAttribute('height', String(ts));
    tok.setAttribute('x', String(-ts / 2));
    tok.setAttribute('y', String(-ts / 2));
    tok.setAttribute('rx', '2');
    return tok;
  },
  chipText: {
    wide: 'agent.publish({ card: "sam" })',
    compact: 'agent.publish()',
  },
};
